import { User } from 'fitify-types/src/types/user'
import { isDefined } from 'fitify-utils/src/properties'

import { DEFAULT_PROMO_CODE } from 'data/promo'
import type { FunnelRoutesDefinition } from 'types/routes'
import { type DataPathState, Funnel, Question } from 'types/state'

import { getSubscriptionFlowPages } from '../../utils/funnel-pages'

type WALLPIF01DataState = DataPathState<Funnel.WALLPIF01>

const pages = {
  welcomeAgeBuckets: {
    path: '/wallpif01/welcome-age-buckets',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    isExcludedFromProgressBarOrStepper: true,
    stateKey: 'age_bucket',
    id: 'welcome_age_buckets',
  },
  socialProofMillions: {
    path: '/wallpif01/social-proof-millions',
    isValidatedStep: true,
    id: 'social_proof_millions',
  },
  experience: {
    path: '/wallpif01/experience',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'experience',
    id: 'experience',
  },
  experiencePitch: {
    path: '/wallpif01/experience-pitch',
    isValidatedStep: true,
    id: 'experience_pitch',
    condition: (data: WALLPIF01DataState) => {
      return isDefined(data.experience)
    },
  },
  mainGoal: {
    path: '/wallpif01/main-goal',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'goal',
    id: 'main_goal',
    required: true,
  },
  goalPitch: {
    path: '/wallpif01/goal-pitch',
    isValidatedStep: true,
    id: 'goal_pitch',
    condition: (data: WALLPIF01DataState) => {
      return isDefined(data.goal)
    },
  },
  motivationEarly: {
    path: '/wallpif01/motivation-early',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'motivation_early',
    id: 'motivation_early',
  },
  didYouKnowHormones: {
    path: '/wallpif01/did-you-know-hormones',
    isValidatedStep: true,
    id: 'did_you_know_hormones',
  },
  didYouKnowDepression: {
    path: '/wallpif01/did-you-know-depression',
    isValidatedStep: true,
    id: 'did_you_know_depression',
  },
  didYouKnowLongevity: {
    path: '/wallpif01/did-you-know-longevity',
    isValidatedStep: true,
    id: 'did_you_know_longevity',
  },
  physicalBuild: {
    path: '/wallpif01/physical-build',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'current_shape',
    id: 'physical_build',
  },
  bodyGoal: {
    path: '/wallpif01/body-goal',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'body_goal',
    id: 'body_goal',
  },
  weightChangeExp: {
    path: '/wallpif01/weight-change-exp',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'weight_change_exp',
    id: 'weight_change_exp',
  },
  bodyTypePitch: {
    path: '/wallpif01/body-type-pitch',
    isValidatedStep: true,
    id: 'body_type_pitch',
    condition: (data: WALLPIF01DataState) => {
      return isDefined(data.weight_change_exp)
    },
  },
  bestShape: {
    path: '/wallpif01/best-shape',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'latest_ideal_weight',
    id: 'best_shape',
  },
  consistencyPitch: {
    path: '/wallpif01/consistency-pitch',
    isValidatedStep: true,
    id: 'consistency_pitch',
  },
  flexible: {
    path: '/wallpif01/flexible',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'flexible',
    id: 'flexible',
  },
  problemAreas: {
    path: '/wallpif01/problem-areas',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'problem_areas',
    id: 'problem_areas',
    required: true,
  },
  problemAreasPitch: {
    path: '/wallpif01/problem-areas-pitch',
    isValidatedStep: true,
    id: 'problem_areas_pitch',
  },
  struggle: {
    path: '/wallpif01/struggle',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'struggle',
    id: 'struggle',
  },
  strugglePitch: {
    path: '/wallpif01/struggle-pitch',
    isValidatedStep: true,
    id: 'struggle_pitch',
    condition: (data: WALLPIF01DataState) => {
      return Boolean(
        data.struggle?.length &&
        data.struggle.some((item) => item !== Question.Struggle.None)
      )
    },
  },
  stairsCardio: {
    path: '/wallpif01/stairs-cardio',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'stairs_cardio',
    id: 'stairs_cardio',
  },
  howOftenExercise: {
    path: '/wallpif01/how-often-exercise',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'how_often_exercise',
    id: 'how_often_exercise',
  },
  exerciseBarrier: {
    path: '/wallpif01/exercise-barrier',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'exercise_barrier',
    id: 'exercise_barrier',
    condition: (data: WALLPIF01DataState) => {
      const howOften = data.how_often_exercise
      return (
        howOften === Question.HowOftenExercise.Never ||
        howOften === Question.HowOftenExercise.Monthly
      )
    },
  },
  workoutLength: {
    path: '/wallpif01/workout-length',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'workout_length',
    id: 'workout_length',
  },
  bigResultsPitch: {
    path: '/wallpif01/big-results-pitch',
    isValidatedStep: true,
    id: 'big_results_pitch',
  },
  howOftenWalks: {
    path: '/wallpif01/how-often-walks',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'how_often_walks',
    id: 'how_often_walks',
  },
  fitnessLevelSquats: {
    path: '/wallpif01/fitness-level-squats',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'fitness_level_squats',
    id: 'fitness_level_squats',
  },
  fitnessLevelCrunches: {
    path: '/wallpif01/fitness-level-crunches',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'fitness_level_crunches',
    id: 'fitness_level_crunches',
  },
  fitnessLevelPlank: {
    path: '/wallpif01/fitness-level-plank',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'fitness_level_plank',
    id: 'fitness_level_plank',
  },
  workSchedule: {
    path: '/wallpif01/work-schedule',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'work_schedule',
    id: 'work_schedule',
  },
  stress: {
    path: '/wallpif01/stress',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'stress',
    id: 'stress',
  },
  cortisolPitch: {
    path: '/wallpif01/cortisol-pitch',
    isValidatedStep: true,
    id: 'cortisol_pitch',
  },
  typicalDayActivity: {
    path: '/wallpif01/typical-day-activity',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'typical_day_activity',
    id: 'typical_day_activity',
  },
  energyLevels: {
    path: '/wallpif01/energy-levels',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'energy_levels',
    id: 'energy_levels',
  },
  energyPitch: {
    path: '/wallpif01/energy-pitch',
    isValidatedStep: true,
    id: 'energy_pitch',
  },
  waterIntake: {
    path: '/wallpif01/water-intake',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'water_intake',
    id: 'water_intake',
  },
  sleepDuration: {
    path: '/wallpif01/sleep-duration',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'sleep_duration',
    id: 'sleep_duration',
  },
  timeBreakfast: {
    path: '/wallpif01/time-breakfast',
    cluster: 'cluster_nutrition',
    isValidatedStep: true,
    stateKey: 'time_breakfast',
    id: 'time_breakfast',
  },
  timeLunch: {
    path: '/wallpif01/time-lunch',
    cluster: 'cluster_nutrition',
    isValidatedStep: true,
    stateKey: 'time_lunch',
    id: 'time_lunch',
  },
  timeDinner: {
    path: '/wallpif01/time-dinner',
    cluster: 'cluster_nutrition',
    isValidatedStep: true,
    stateKey: 'time_dinner',
    id: 'time_dinner',
  },
  diet: {
    path: '/wallpif01/diet',
    cluster: 'cluster_nutrition',
    isValidatedStep: true,
    stateKey: 'diet',
    id: 'diet',
  },
  habits: {
    path: '/wallpif01/habits',
    cluster: 'cluster_nutrition',
    isValidatedStep: true,
    stateKey: 'habits',
    id: 'habits',
  },
  cravings: {
    path: '/wallpif01/cravings',
    cluster: 'cluster_nutrition',
    isValidatedStep: true,
    stateKey: 'cravings',
    id: 'cravings',
  },
  lifeEventsStopped: {
    path: '/wallpif01/life-events-stopped',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'life_events_stopped',
    id: 'life_events_stopped',
  },
  eligibilityPitch: {
    path: '/wallpif01/eligibility-pitch',
    isValidatedStep: true,
    id: 'eligibility_pitch',
  },
  mainMotivation: {
    path: '/wallpif01/main-motivation',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'main_motivation',
    id: 'main_motivation',
  },
  height: {
    path: '/wallpif01/height',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'height',
    id: 'height',
    required: true,
  },
  weight: {
    path: '/wallpif01/weight',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'weight',
    id: 'weight',
    required: true,
  },
  goalWeight: {
    path: '/wallpif01/goal-weight',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'goal_weight',
    id: 'goal_weight',
    required: true,
  },
  age: {
    path: '/wallpif01/age',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'age',
    id: 'age',
    required: true,
  },
  wellnessProfile: {
    path: '/wallpif01/wellness-profile',
    isValidatedStep: true,
    id: 'wellness_profile',
    condition: (data: WALLPIF01DataState) => {
      return (
        isDefined(data.typical_day_activity) &&
        isDefined(data.current_shape) &&
        isDefined(data.how_often_exercise) &&
        isDefined(data.weight_change_exp)
      )
    },
  },
  lifeEvent: {
    path: '/wallpif01/life-event',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'life_event',
    id: 'life_event',
  },
  lifeEventDate: {
    path: '/wallpif01/life-event-date',
    isValidatedStep: true,
    stateKey: 'life_event_date',
    id: 'life_event_date',
    condition: (data: WALLPIF01DataState) => {
      return (
        isDefined(data.life_event) &&
        data.life_event !== User.Profile.LifeEvent.NoEvent
      )
    },
  },
  lifeEventGraph: {
    path: '/wallpif01/life-event-graph',
    isValidatedStep: true,
    id: 'life_event_graph',
    condition: (data: WALLPIF01DataState) => {
      return isDefined(data.goal_weight) && isDefined(data.weight)
    },
  },
  credibilityPitch: {
    path: '/wallpif01/credibility-pitch',
    isValidatedStep: true,
    id: 'credibility_pitch',
  },
  planGenerator: {
    path: '/wallpif01/plan-generator',
    id: 'plan_generator',
  },
  email: {
    path: '/wallpif01/email',
    id: 'email',
    stateKey: 'email',
  },
  name: {
    path: '/wallpif01/name',
    isValidatedStep: true,
    stateKey: 'displayName',
    id: 'name',
  },
  namePitch: {
    path: '/wallpif01/name-pitch',
    isValidatedStep: true,
    id: 'name_pitch',
  },
  discountReveal: {
    path: '/wallpif01/discount-reveal',
    isValidatedStep: true,
    id: 'discount_reveal',
    condition: (_, { promoCode }) => {
      return Boolean(!promoCode || promoCode === DEFAULT_PROMO_CODE)
    },
  },
  ...getSubscriptionFlowPages(Funnel.WALLPIF01),
} as const satisfies FunnelRoutesDefinition<WALLPIF01DataState>['pages']

const pagesMap = Object.values(pages)

const detectLocationPagesMap = [
  pages.planGenerator,
  pages.subscription,
  pages.exclusiveOffer,
] as const satisfies FunnelRoutesDefinition<WALLPIF01DataState>['detectLocationPagesMap']

const noLeaveDialogPagesMap = [
  pages.subscription,
  pages.exclusiveOffer,
  pages.success,
] as const satisfies FunnelRoutesDefinition<WALLPIF01DataState>['noLeaveDialogPagesMap']

export const WALLPIF01_ROUTES = {
  detectLocationPagesMap,
  noLeaveDialogPagesMap,
  pages,
  pagesMap,
} as const satisfies FunnelRoutesDefinition<WALLPIF01DataState>
